import { askText } from '../components/ask';
import { useState, type FormEvent } from 'react';
import { get, post, put } from '../api';
import { Problems } from '../components/Field';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Tag {
  id: number;
  label: string;
}

/** Tags group customers and assignments (by area, service or skill) so contractors see the work that suits them. */
export function Tags() {
  const [tick, setTick] = useState(0);
  const [label, setLabel] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const active = useLoad(() => get<{ items: Tag[] }>('/api/tags'), [tick]);
  const off = useLoad(() => get<{ items: Tag[] }>('/api/tags?state=inactive'), [tick]);

  async function act(run: () => Promise<unknown>) {
    setProblems([]);
    try {
      await run();
      setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    await act(async () => {
      await post('/api/tags', { label });
      setLabel('');
    });
  }

  async function rename(t: Tag) {
    const next = await askText({
      title: 'Rename tag',
      label: 'Tag name',
      value: t.label,
      required: true,
      confirmLabel: 'Rename',
    });
    if (next && next.trim() !== t.label) await act(() => put(`/api/tags/${t.id}`, { label: next }));
  }

  const items = active.data?.items ?? [];
  const inactive = off.data?.items ?? [];
  return (
    <>
      <h1>Tags</h1>
      <form onSubmit={add} className="filters">
        <label>
          New tag
          <input value={label} onChange={(e) => setLabel(e.target.value)} required />
        </label>
        <button type="submit">Add tag</button>
      </form>
      <Problems problems={problems} title="Could not do that." />
      {active.error && (
        <p role="alert" className="error">
          {active.error}
        </p>
      )}
      {!active.loading && items.length === 0 && <p className="empty">No tags yet.</p>}
      <ul className="plain">
        {items.map((t) => (
          <li key={t.id} className="row">
            <span>{t.label}</span>
            <span className="pager">
              <button type="button" className="quiet" onClick={() => void rename(t)}>
                Rename
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => void act(() => post(`/api/tags/${t.id}/active`, { active: false }))}
              >
                Deactivate
              </button>
            </span>
          </li>
        ))}
      </ul>
      {inactive.length > 0 && (
        <>
          <h2>Deactivated</h2>
          <ul className="plain">
            {inactive.map((t) => (
              <li key={t.id} className="row">
                <span className="muted">{t.label}</span>
                <button
                  type="button"
                  className="quiet"
                  onClick={() => void act(() => post(`/api/tags/${t.id}/active`, { active: true }))}
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
